import { useId, useRef, useState, type ReactNode } from 'react'
import { useLang } from '../i18n'
import { api } from '../lib/api'
import { normalizeThorId } from '../lib/thor'

/** What a Thor hit hands the form: the canonical id, and the names to prefill (null = Thor has none). */
export interface ThorHit {
  thorId: string
  client: string | null
  /** Thor's opportunity name — the BID NAME (a bid has no opportunity field of its own). */
  opportunity: string | null
}

type Status = 'idle' | 'looking' | 'found' | 'not_found' | 'unavailable' | 'invalid'

const STATUS_KEY = {
  looking: 'thor_looking',
  found: 'thor_found',
  not_found: 'thor_not_found',
  unavailable: 'thor_unavailable',
  invalid: 'thor_format_hint',
} as const

/**
 * The Thor ID (OP#) input shared by the new-bid form and Bid Settings: an input plus a Look up
 * button, which asks Thor (through the backend — the flow's URL is a credential) for the client and
 * opportunity names so the form can prefill them (the opportunity name goes into the bid name).
 *
 * Committing the value (Enter or blur) normalises it: a valid one is shown as `OP# 12345678` and
 * looked up when it differs from the last value looked up; a malformed one gets the format hint and
 * makes no call. The button is the explicit retry. Every outcome is announced in a polite status
 * line, and none of them blocks the form: a miss or an outage just means typing the fields by hand.
 *
 * A response for a value the member has since changed is dropped, so a slow answer can never
 * prefill the form with the wrong opportunity.
 */
export function ThorIdField({
  value,
  onChange,
  onFound,
  label,
  disabled = false,
  warning,
}: {
  value: string
  onChange: (value: string) => void
  onFound: (hit: ThorHit) => void
  /** The field's label, so each form can mark it as it marks its other fields. */
  label: ReactNode
  disabled?: boolean
  /** A non-blocking note under the field (the "another of your bids has this Thor ID" warning). */
  warning?: string | null
}) {
  const { t } = useLang()
  const inputId = useId()
  const statusId = useId()
  const [status, setStatus] = useState<Status>('idle')
  // The last id actually sent to Thor — an unchanged value is not looked up twice on blur.
  const lastLooked = useRef<string | null>(null)
  // Bumped whenever the member types: an in-flight answer carrying an older number is dropped.
  const seq = useRef(0)
  // The LATEST onFound: an answer arriving seconds later must be applied to the form as it is
  // then (a name typed meanwhile, say), not as it was when the lookup started.
  const onFoundRef = useRef(onFound)
  onFoundRef.current = onFound

  async function lookup(thorId: string): Promise<void> {
    lastLooked.current = thorId
    const mine = ++seq.current
    setStatus('looking')
    const res = await api.lookupThorOpportunity(thorId)
    if (mine !== seq.current) return
    setStatus(res.status)
    if (res.status === 'found') {
      onFoundRef.current({ thorId: res.thorId, client: res.client, opportunity: res.opportunity })
    }
  }

  /** Enter / blur: normalise, then look up if this is a number Thor has not been asked about yet. */
  function commit(force: boolean): void {
    if (value.trim() === '') {
      // Nothing to look up. Forgetting the last lookup lets the same number be looked up again
      // after it was cleared and re-entered.
      lastLooked.current = null
      setStatus('idle')
      return
    }
    const thorId = normalizeThorId(value)
    if (!thorId) {
      seq.current++
      setStatus('invalid')
      return
    }
    if (thorId !== value) onChange(thorId)
    if (force || thorId !== lastLooked.current) void lookup(thorId)
  }

  const looking = status === 'looking'
  const message = status === 'idle' ? '' : t(STATUS_KEY[status])

  return (
    <div className="thor-field">
      <label className="prompt-label" htmlFor={inputId}>
        {label}
      </label>
      <div className="thor-row">
        <input
          id={inputId}
          className="prompt-input"
          value={value}
          placeholder="OP# 12345678"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          aria-invalid={status === 'invalid' || undefined}
          aria-describedby={message ? statusId : undefined}
          onChange={(e) => {
            // Typing retires whatever answer is in flight — it was for a number no longer here.
            seq.current++
            setStatus('idle')
            onChange(e.target.value)
          }}
          onBlur={() => commit(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Inside a form, Enter would submit it: here it means "look this number up".
              e.preventDefault()
              commit(false)
            }
          }}
        />
        {!disabled && (
          <button
            type="button"
            className="mini thor-lookup"
            disabled={looking || value.trim() === ''}
            onClick={() => commit(true)}
          >
            {looking && <span className="thor-spinner" aria-hidden="true" />}
            {t('thor_lookup')}
          </button>
        )}
      </div>
      <p
        id={statusId}
        className={'thor-status' + (status === 'invalid' ? ' thor-status-error' : '')}
        role="status"
        aria-live="polite"
      >
        {message}
      </p>
      {warning && <p className="thor-warn">{warning}</p>}
    </div>
  )
}
